"use client";

import { useEffect, useState, useRef } from "react";

interface ResultCardProps {
  label: string;
  value: number | string;
  prefix?: string;
  suffix?: string;
  subtitle?: string;
  locale?: string;
  trend?: "positive" | "negative" | "neutral";
  color?: "primary" | "secondary" | "orange" | "danger" | "neutral";
}

export default function ResultCard({
  label,
  value,
  prefix = "",
  suffix = "",
  subtitle,
  locale = "en-US",
  trend = "neutral",
  color = "neutral",
}: ResultCardProps) {
  const [animatedValue, setAnimatedValue] = useState(value);
  const previousValueRef = useRef(value);

  useEffect(() => {
    if (typeof value === "string") {
      setAnimatedValue(value);
      previousValueRef.current = value;
      return;
    }

    const start = typeof previousValueRef.current === "number" ? previousValueRef.current : 0;
    const end = typeof value === "number" ? value : 0;
    if (start === end) {
      // A cancelled mid-flight animation can leave animatedValue stuck between
      // start and end; snap to the target so the card never displays a stale
      // intermediate number after rapid input changes.
      setAnimatedValue(end);
      previousValueRef.current = end;
      return;
    }

    const duration = 400; // ms
    const startTime = performance.now();

    let animationFrameId: number;

    const updateNumber = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function: easeOutQuad
      const ease = progress * (2 - progress);
      const current = start + (end - start) * ease;

      setAnimatedValue(current);

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(updateNumber);
      } else {
        previousValueRef.current = end;
      }
    };

    animationFrameId = requestAnimationFrame(updateNumber);

    return () => {
      cancelAnimationFrame(animationFrameId);
      // If this run is interrupted by a new value, record its target so the
      // next animation starts from where this one was headed, never from a
      // stale baseline that could equal the new target and freeze the card.
      previousValueRef.current = end;
    };
  }, [value]);

  // Format animated value
  const formatVal = (val: number | string) => {
    if (typeof val === "string") return val;
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: val % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(val);
  };

  const getColorClass = () => {
    switch (color) {
      case "primary":
        return "card-primary";
      case "secondary":
        return "card-secondary";
      case "orange":
        return "card-orange";
      case "danger":
        return "card-danger";
      default:
        // Automatically color code based on trend
        if (trend === "positive") return "card-primary";
        if (trend === "negative") return "card-danger";
        return "card-neutral";
    }
  };

  const valStr = formatVal(animatedValue);

  return (
    <div className={`card result-card ${getColorClass()}`}>
      <span className="result-label">{label}</span>
      <div className={`result-value-container ${valStr.length > 16 ? "text-xs-val" : valStr.length > 12 ? "text-sm-val" : ""}`}>
        {prefix && typeof animatedValue !== "string" && <span className="value-prefix">{prefix}</span>}
        <span className="value-number">{valStr}</span>
        {suffix && typeof animatedValue !== "string" && <span className="value-suffix">{suffix}</span>}
      </div>
      {subtitle && <span className="result-subtitle">{subtitle}</span>}

      <style jsx>{`
        .result-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: var(--space-2) var(--space-3);
          min-height: 72px;
          border-left: 3px solid var(--border-color);
          transition: all var(--transition-normal);
          min-width: 0;
          width: 100%;
          box-sizing: border-box;
        }
        .result-card:hover {
          transform: translateY(-1px);
        }
        .result-label {
          font-family: var(--font-heading);
          font-size: var(--text-2xs);
          font-weight: var(--fw-extrabold);
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: var(--text-secondary);
          margin-bottom: var(--space-1);
          max-width: 100%;
          word-break: break-word;
          overflow-wrap: break-word;
        }
        .result-value-container {
          display: flex;
          align-items: baseline;
          justify-content: center;
          gap: 0.2rem;
          font-family: var(--font-heading);
          font-weight: var(--fw-extrabold);
          font-size: var(--text-lg);
          line-height: 1.15;
          color: var(--text-primary);
          max-width: 100%;
          flex-wrap: wrap;
          word-break: break-all;
          overflow-wrap: anywhere;
        }
        @media (min-width: 768px) {
          .result-value-container {
            font-size: var(--text-xl);
          }
        }
        .text-sm-val {
          font-size: var(--text-base) !important;
        }
        .text-xs-val {
          font-size: var(--text-sm) !important;
        }
        @media (min-width: 768px) {
          .text-sm-val {
            font-size: var(--text-md) !important;
          }
          .text-xs-val {
            font-size: var(--text-sm) !important;
          }
        }
        .value-prefix {
          font-size: var(--text-sm);
          color: var(--text-muted);
          font-weight: var(--fw-bold);
        }
        .value-number {
          font-variant-numeric: tabular-nums;
        }
        .value-suffix {
          font-size: var(--text-2xs);
          color: var(--text-muted);
          font-weight: var(--fw-bold);
        }
        .result-subtitle {
          margin-top: var(--space-1);
          font-size: var(--text-2xs);
          color: var(--text-muted);
          font-weight: var(--fw-bold);
        }

        /* Color Variants based on Border & Glow */
        .card-primary {
          border-left-color: var(--primary);
          background: linear-gradient(135deg, var(--bg-surface) 0%, var(--primary-a02) 100%);
          box-shadow: var(--card-shadow);
        }
        .card-primary:hover {
          border-left-color: var(--primary-hover);
          box-shadow: 0 6px 20px -2px var(--primary-a12), var(--card-shadow);
        }
        .card-primary .value-number {
          color: var(--primary);
        }
        .card-secondary {
          border-left-color: var(--secondary);
          background: linear-gradient(135deg, var(--bg-surface) 0%, var(--secondary-a02) 100%);
          box-shadow: var(--card-shadow);
        }
        .card-secondary:hover {
          border-left-color: var(--secondary-hover);
          box-shadow: 0 6px 20px -2px var(--secondary-a12), var(--card-shadow);
        }
        .card-secondary .value-number {
          color: var(--secondary);
        }
        .card-orange {
          border-left-color: var(--accent-orange);
          background: linear-gradient(135deg, var(--bg-surface) 0%, var(--orange-a02) 100%);
          box-shadow: var(--card-shadow);
        }
        .card-orange:hover {
          border-left-color: var(--accent-orange-hover);
          box-shadow: 0 6px 20px -2px var(--orange-a12), var(--card-shadow);
        }
        .card-orange .value-number {
          color: var(--accent-orange);
        }
        .card-danger {
          border-left-color: var(--danger);
          background: linear-gradient(135deg, var(--bg-surface) 0%, var(--danger-a02) 100%);
          box-shadow: var(--card-shadow);
        }
        .card-danger:hover {
          border-left-color: var(--danger-hover);
          box-shadow: 0 6px 20px -2px var(--danger-a12), var(--card-shadow);
        }
        .card-danger .value-number {
          color: var(--danger);
        }
        .card-neutral {
          border-left-color: var(--border-color);
          box-shadow: var(--card-shadow);
        }
        .card-neutral:hover {
          border-left-color: var(--border-color-hover);
        }
      `}</style>
    </div>
  );
}
